import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Cookies from 'js-cookie';

const MyAddOns = () => {
  const [userAddOns, setUserAddOns] = useState([]);
  const [addOnServices, setAddOnServices] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchUserAddOns();
    fetchAddOnServices();
  }, []);

  const fetchUserAddOns = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = Cookies.get('enakid_token');
      const response = await fetch(`${backendUrl}/api/user-add-ons`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserAddOns(data);
      }
    } catch (error) {
      console.error('Error fetching user add-ons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddOnServices = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = Cookies.get('enakid_token');
      const response = await fetch(`${backendUrl}/api/add-on-services`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const servicesMap = {};
        data.forEach(service => {
          servicesMap[service.id] = service;
        });
        setAddOnServices(servicesMap);
      }
    } catch (error) {
      console.error('Error fetching add-on services:', error);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'capacity': '👥',
      'features': '⚡',
      'support': '🎯',
      'storage': '💾',
      'analytics': '📊'
    };
    return icons[category] || '🔧';
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'expired': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit || limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (userAddOns.length === 0) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No add-ons purchased</h3>
          <p className="text-gray-600 mb-6">
            Enhance your plan with additional features and capabilities
          </p>
          <Button onClick={() => window.location.href = '/add-ons'}>
            Browse Add-ons
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Add-ons</h1>
        <p className="text-gray-600">Manage your purchased add-on services</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {userAddOns.length}
              </div>
              <div className="text-sm text-gray-600">Total Add-ons</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {userAddOns.filter(addon => addon.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {userAddOns.filter(addon => addon.status === 'expired').length}
              </div>
              <div className="text-sm text-gray-600">Expired</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {formatPrice(userAddOns.reduce((sum, addon) => sum + addon.total_price, 0))}
              </div>
              <div className="text-sm text-gray-600">Total Spent</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add-ons List */}
      <div className="space-y-4">
        {userAddOns.map((userAddOn) => {
          const service = addOnServices[userAddOn.add_on_service_id];
          if (!service) return null;

          const usagePercentage = getUsagePercentage(userAddOn.usage_count, userAddOn.usage_limit);

          return (
            <Card key={userAddOn.id}>
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <span className="text-3xl">{getCategoryIcon(service.category)}</span>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{service.display_name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                        
                        <div className="flex items-center space-x-4 mt-3">
                          <Badge className={getStatusColor(userAddOn.status)}>
                            {userAddOn.status.charAt(0).toUpperCase() + userAddOn.status.slice(1)}
                          </Badge>
                          
                          {userAddOn.billing_cycle && (
                            <Badge variant="outline">
                              {userAddOn.billing_cycle === 'monthly' ? 'Monthly' : 'Annual'}
                            </Badge>
                          )}
                          
                          {userAddOn.quantity > 1 && (
                            <span className="text-sm text-gray-600">
                              Qty: {userAddOn.quantity}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatPrice(userAddOn.total_price)}
                        </div>
                        {userAddOn.quantity > 1 && (
                          <div className="text-sm text-gray-600">
                            {formatPrice(userAddOn.unit_price)} each
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Usage Information */}
                    {userAddOn.usage_limit && userAddOn.usage_limit > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-600">Usage</span>
                          <span className="text-gray-900">
                            {userAddOn.usage_count} / {userAddOn.usage_limit}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(usagePercentage)}`}
                            style={{ width: `${usagePercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Dates Information */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                      <div>
                        <span className="text-gray-500">Purchased</span>
                        <div className="font-medium">{formatDate(userAddOn.purchased_at)}</div>
                      </div>
                      
                      {userAddOn.expires_at && (
                        <div>
                          <span className="text-gray-500">Expires</span>
                          <div className="font-medium">{formatDate(userAddOn.expires_at)}</div>
                        </div>
                      )}
                      
                      {userAddOn.next_billing_date && userAddOn.status === 'active' && (
                        <div>
                          <span className="text-gray-500">Next Billing</span>
                          <div className="font-medium">{formatDate(userAddOn.next_billing_date)}</div>
                        </div>
                      )}
                      
                      <div>
                        <span className="text-gray-500">Category</span>
                        <div className="font-medium">{service.category.replace('_', ' ')}</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2 mt-4">
                      {service.setup_required && userAddOn.status === 'active' && (
                        <Button variant="outline" size="sm">
                          Manage Setup
                        </Button>
                      )}
                      
                      {userAddOn.status === 'active' && userAddOn.billing_cycle && (
                        <Button variant="outline" size="sm" className="text-red-600">
                          Cancel Subscription
                        </Button>
                      )}
                      
                      {userAddOn.status === 'expired' && (
                        <Button variant="outline" size="sm">
                          Renew
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MyAddOns;